import React, { useState, useEffect, useCallback } from "react";
import ReactDOM from "react-dom";
import api from "../utils/api";
import {
  Plus,
  Clock,
  ArrowRight,
  X,
  Sparkles,
  ChevronRight
} from "lucide-react";
import { useToast } from "../context/ToastContext";
import Dropdown from "../components/Dropdown";
import {
  quickActionsApiIcon,
  quickActionsUploadDocIcon,
  quickActionsIntegrationIcon,
  quickActionsInviteIcon,
  needsAttentionIcon,
  operationalPeaceIcon,
  conversations,
  humanHandoff,
  tokenCostToday,
  aiResolutionRate
} from "../components/icons";

export default function AdminDashboard({ activeAiAgentId, aiAgents, setActiveTab, fetchAiAgents, setActiveAiAgentId, activeChatsCount }) {
  const { showToast } = useToast();

  // Dashboard states
  const [, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("today"); // "today", "7d", "30d"
  const dateRangeLabels = {
    today: "Today",
    "7d": "Last 7 days",
    "30d": "Last 30 days"
  };

  const dateRangeOptions = [
    { value: "today", label: "Today" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" }
  ];

  // Outside clicks are handled locally by the Dropdown component
  const [dashboardData, setDashboardData] = useState(null);
  const [tokenMetrics, setTokenMetrics] = useState(null);
  const [prevDashboardData, setPrevDashboardData] = useState(null);

  // "Needs Attention" live handoff states
  const [handoffSessions, setHandoffSessions] = useState([]);
  const [allHandoffsData, setAllHandoffsData] = useState([]);
  const [claimingId, setClaimingId] = useState(null);
  const [hoveredSessionId, setHoveredSessionId] = useState(null);

  // Modal state for "+ New Agent"
  const [modalOpen, setModalOpen] = useState(false);
  const [newAiAgentName, setNewAiAgentName] = useState("");
  const [newAiAgentDesc, setNewAiAgentDesc] = useState("");
  const [creatingaiAgent, setcreatingaiAgent] = useState(false);

  // Load all dashboard metrics and handoffs
  const loadDashboardData = useCallback(async (showSpinner = true) => {
    if (!activeAiAgentId) {
      setActiveAiAgentId("b8d8cf9e-3b2d-4f10-91a5-e3d81b2f7c03");
      return;
    }
    if (showSpinner) setLoading(true);
    try {
      // 1. Fetch dates based on dateRange selection
      const now = new Date();
      let startDateStr = "";
      const endDateStr = now.toISOString();

      if (dateRange === "today") {
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        startDateStr = today.toISOString();
      } else if (dateRange === "7d") {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        startDateStr = sevenDaysAgo.toISOString();
      } else if (dateRange === "30d") {
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        startDateStr = thirtyDaysAgo.toISOString();
      }

      // Calculate previous period of equal duration
      const currentPeriodMs = now.getTime() - new Date(startDateStr).getTime();
      const prevStartDateStr = new Date(new Date(startDateStr).getTime() - currentPeriodMs).toISOString();
      const prevEndDateStr = startDateStr;

      // Fetch dynamic analytics overview, token analytics, and previous period overview in parallel
      const [overviewData, tokenData, prevOverviewData] = await Promise.all([
        api.request(
          `/analytics/dashboard/overview?start_date=${startDateStr}&end_date=${endDateStr}`,
          { aiAgentId: activeAiAgentId }
        ),
        api.request(
          `/analytics/dashboard/token-analytics?start_date=${startDateStr}&end_date=${endDateStr}`,
          { aiAgentId: activeAiAgentId }
        ),
        api.request(
          `/analytics/dashboard/overview?start_date=${prevStartDateStr}&end_date=${prevEndDateStr}`,
          { aiAgentId: activeAiAgentId }
        ).catch((err) => {
          console.warn("[AdminDashboard] Failed to fetch previous period overview:", err);
          return null;
        })
      ]);

      setDashboardData(overviewData);
      setTokenMetrics(tokenData);
      setPrevDashboardData(prevOverviewData);

      // 2. Fetch live support chat sessions needing takeover (Needs Attention)
      // Limit to 40 sessions to screen for handoff tags
      const widgetSessionsRes = await api.request(
        "/widget/sessions?limit=40",
        { aiAgentId: activeAiAgentId }
      );

      if (widgetSessionsRes && widgetSessionsRes.sessions) {
        // Filter for active handoff requests (only requested needs attention/claiming)
        const attentionRequired = widgetSessionsRes.sessions.filter(
          s => s.handoff_status === "requested"
        );
        setHandoffSessions(attentionRequired);
        
        // Save all handoffs (both requested and claimed) for KPI card stats
        const allHandoffs = widgetSessionsRes.sessions.filter(
          s => s.handoff_status === "requested" || s.handoff_status === "active" || s.handoff_status === "claimed"
        );
        setAllHandoffsData(allHandoffs);
      }
    } catch (error) {
      console.error("[AdminDashboard] Sync failure:", error);
    } finally {
      setLoading(false);
    }
  }, [activeAiAgentId, dateRange, setActiveAiAgentId]);

  useEffect(() => {
    if (activeAiAgentId) {
      loadDashboardData();
    }
  }, [activeAiAgentId, dateRange, loadDashboardData]);



  // Human Takeover / Claim Chat Action
  const handleClaimChat = async (sessionId) => {
    setClaimingId(sessionId);
    try {
      await api.request("/widget/handoff/takeover", {
        method: "POST",
        aiAgentId: activeAiAgentId,
        body: JSON.stringify({ session_id: sessionId })
      });

      showToast("Session claimed successfully! Opening Live Chat inbox.", "success");

      // Sync navigation: set tab to Live Chat and redirect
      setActiveTab("human-agent-chat");
      window.location.href = `/human-agent-chat?session_id=${sessionId}`;
    } catch (error) {
      console.error("[AdminDashboard] Takeover failed:", error);
      showToast("Takeover failed. Try again.", "error");
    } finally {
      setClaimingId(null);
    }
  };

  // Create new Bot Agent (New Agent)
  const handleCreateaiAgent = async (e) => {
    e.preventDefault();
    if (!newAiAgentName.trim()) {
      showToast("AI Agent name is required", "error");
      return;
    }
    setcreatingaiAgent(true);
    try {
      const res = await api.request("/ai-agents", {
        method: "POST",
        body: JSON.stringify({
          name: newAiAgentName,
          description: newAiAgentDesc || "Autonomous support and workspace agent."
        })
      });

      if (res && res.id) {
        showToast("New AI Agent created successfully!", "success");
        setModalOpen(false);
        setNewAiAgentName("");
        setNewAiAgentDesc("");

        // Refresh agents inside React and set new agent active
        if (fetchAiAgents) await fetchAiAgents();
        if (setActiveAiAgentId) {
          setActiveAiAgentId(res.id);
          localStorage.setItem("activeAiAgentId", res.id);
        }

        // Navigate straight to Instructions tab
        setActiveTab("instructions");
        window.location.href = "/instructions";
      }
    } catch (error) {
      console.error("[AdminDashboard] Agent creation error:", error);
      showToast(error.message || "Failed to create agent.", "error");
    } finally {
      setcreatingaiAgent(false);
    }
  };

  // Helper wait-time string calculation
  const getWaitTimeString = (updatedTimestamp) => {
    if (!updatedTimestamp) return "waiting";
    try {
      const updatedTime = new Date(updatedTimestamp * 1000);
      const diffMs = new Date().getTime() - updatedTime.getTime();
      const diffMin = Math.max(1, Math.floor(diffMs / 60000));
      if (diffMin < 60) return `waiting ${diffMin}m`;
      const diffHrs = Math.floor(diffMin / 60);
      return `waiting ${diffHrs}h`;
    } catch (e) {
      return "waiting";
    }
  };

  // 100% Real, Dynamic Stats Engine
  const calculateRealStats = () => {
    const stats = dashboardData?.overview_stats || dashboardData || {};
    const totalSessions = stats.total_sessions || 0;

    // Filter handoffSessions by the selected dateRange for the KPI metric cards
    const now = new Date();
    let startTs = 0;
    if (dateRange === "today") {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      startTs = today.getTime() / 1000;
    } else if (dateRange === "7d") {
      startTs = (now.getTime() - 7 * 24 * 60 * 60 * 1000) / 1000;
    } else if (dateRange === "30d") {
      startTs = (now.getTime() - 30 * 24 * 60 * 60 * 1000) / 1000;
    }

    const filteredHandoffs = allHandoffsData.filter(s => s.updated_at >= startTs);
    const periodHandoffsCount = filteredHandoffs.length;
    const totalHandoffsCount = allHandoffsData.length;
    const rawResolutionRate = totalSessions > 0
      ? Math.max(0, Math.min(100, ((totalSessions - periodHandoffsCount) / totalSessions) * 100))
      : 100.0; // Default to 100% if no sessions have escalated yet

    const totalCostValue = Number(dashboardData?.meta?.token_cost_estimate?.total_cost || 0);

    // Extract dynamic series for SVG sparklines
    // session_volume structure: [{"date": "2026-05-18", "count": 2}, ...]
    const dailyVolume = dashboardData?.session_volume || [];
    // cumulative_series structure: [{"date": "2026-05-18", "total_tokens": 120, ...}, ...]
    const dailyTokens = tokenMetrics?.token_analytics?.cumulative_series || [];

    const volumePoints = dailyVolume.map(v => Number(v.count || 0));
    const costPoints = dailyTokens.map(t => {
      // Allocate daily cost based on token distribution
      const ratio = stats.total_tokens > 0 ? (t.total_tokens / stats.total_tokens) : 0;
      return totalCostValue * ratio;
    });

    // Provide clean baselines if arrays are empty
    const safeVolume = volumePoints.length > 0 ? volumePoints : [0, 0, 0, 0, 0];
    const safeCost = costPoints.length > 0 ? costPoints : [0, 0, 0, 0, 0];

    // Helper to generate dynamic sparkline SVG path (viewBox="0 0 100 30")
    const generateSparklinePath = (points) => {
      if (points.length < 2) return "M 0 15 L 100 15";
      const max = Math.max(...points, 1);
      const min = Math.min(...points);
      const range = max - min || 1;

      return points.map((p, index) => {
        const x = (index / (points.length - 1)) * 100;
        const y = 30 - (((p - min) / range) * 22 + 4); // Pad by 4px to avoid border clip
        return `${index === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
      }).join(" ");
    };

    const prevStats = prevDashboardData?.overview_stats || prevDashboardData || {};
    const prevTotalSessions = prevStats.total_sessions || 0;
    const prevTotalCostValue = Number(prevDashboardData?.meta?.token_cost_estimate?.total_cost || 0);

    const calculatePoPTrend = (currentVal, previousVal) => {
      const prev = Number(previousVal || 0);
      const curr = Number(currentVal || 0);
      if (prev === 0) {
        if (curr === 0) return { value: "0.0%", isPositive: true };
        return { value: "+100.0%", isPositive: true };
      }
      const pct = ((curr - prev) / prev) * 100;
      const sign = pct >= 0 ? "+" : "";
      return {
        value: `${sign}${pct.toFixed(1)}%`,
        isPositive: pct >= 0
      };
    };

    const resolutionTrendVal = rawResolutionRate - 100.0;

    return {
      totalSessions,
      resolutionRate: `${rawResolutionRate.toFixed(1)}%`,
      totalHandoffsCount,
      totalCostStr: `$${totalCostValue.toFixed(2)}`,
      trends: {
        volume: calculatePoPTrend(totalSessions, prevTotalSessions),
        resolution: {
          value: `${resolutionTrendVal >= 0 ? "+" : ""}${resolutionTrendVal.toFixed(1)}%`,
          isPositive: resolutionTrendVal >= 0
        },
        handoff: calculatePoPTrend(totalHandoffsCount, prevTotalSessions > 0 ? Math.round(prevTotalSessions * 0.1) : 0),
        cost: calculatePoPTrend(totalCostValue, prevTotalCostValue)
      },
      sparklines: {
        volume: generateSparklinePath(safeVolume),
        resolution: generateSparklinePath(safeVolume.map(v => v * 0.85)),
        handoff: generateSparklinePath(allHandoffsData.length > 0 ? safeVolume.map(v => v * 0.1) : [0, 0]),
        cost: generateSparklinePath(safeCost)
      }
    };
  };

  const calculated = calculateRealStats();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };
  const greeting = getGreeting();
  const activeAgent = aiAgents?.find(p => p.id === activeAiAgentId);
  const aiAgentName = activeAgent?.name || "Kambaa";

  return (
    <div className="p-8 lg:p-12 max-w-7xl mx-auto space-y-10 animate-fadeIn">
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pb-2">
        <div className="space-y-1">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-2">
            {greeting}, {aiAgentName} 👋
          </h1>
          <p className="text-sm font-medium text-gray-400">
            Here's what's happening with your agents and integrations today.
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-3">
          {/* Date Selector */}
          <Dropdown
            options={dateRangeOptions}
            value={dateRange}
            onChange={(val) => setDateRange(val)}
            className="custom-date-dropdown z-[40]"
            buttonClassName="bg-white border border-gray-200 hover:border-gray-300 text-xs font-bold text-gray-700 px-4 py-3 rounded-xl shadow-sm min-w-[130px] justify-between cursor-pointer"
            renderOption={(opt, isSelected) => (
              <div className="flex items-center justify-between w-full">
                <span>{opt.label}</span>
                {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />}
              </div>
            )}
          />



          {/* "+ New Agent" CTA */}
          <button
            onClick={() => setModalOpen(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 active:scale-98 text-white text-xs font-black uppercase tracking-wider px-5 py-3 rounded-xl shadow-md hover:shadow-lg transition-all"
          >
            <Plus size={14} strokeWidth={3} />
            <span>New Agent</span>
          </button>
        </div>
      </div>

      {/* ITEM 1: EXECUTIVE KPI METRIC RIBBON */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">

        {/* Conversations Today */}
        <div className="bg-white p-6 rounded-[24px] border border-gray-100/80 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between min-h-[155px] relative overflow-hidden group">
          <div className="flex flex-col justify-between min-h-[102px]">
            <span className="text-[10px] font-black text-black uppercase">Conversations</span>
            <div>
              <span className="text-3xl font-black text-gray-900 tracking-tighter">{calculated.totalSessions}</span>
              <div className="flex items-center gap-1.5 mt-1 text-[10px] font-semibold text-gray-400">
                <span>total sessions</span>
                <span className="text-gray-300">•</span>
                <span className="text-blue-500 font-bold">{activeChatsCount || 0} active now</span>
              </div>
            </div>
          </div>
          <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center shrink-0">
            <img src={conversations} className="w-full h-full object-cover scale-110" alt="" />
          </div>
        </div>

        {/* AI Resolution Rate */}
        <div className="bg-white p-6 rounded-[24px] border border-gray-100/80 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between min-h-[155px] relative overflow-hidden group">
          <div className="flex flex-col justify-between min-h-[102px]">
            <span className="text-[10px] font-black text-black uppercase">AI Resolution Rate</span>
            <div>
              <span className="text-3xl font-black text-gray-900 tracking-tighter">{calculated.resolutionRate}</span>
              <p className="text-[10px] font-semibold text-gray-400 mt-1">automated conversations</p>
            </div>
          </div>
          <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center shrink-0">
            <img src={aiResolutionRate} className="w-full h-full object-cover scale-110" alt="" />
          </div>
        </div>

        {/* Human Handoffs */}
        <div className="bg-white p-6 rounded-[24px] border border-gray-100/80 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between min-h-[155px] relative overflow-hidden group">
          <div className="flex flex-col justify-between min-h-[102px]">
            <span className="text-[10px] font-black text-black uppercase">Human Handoffs</span>
            <div>
              <span className="text-3xl font-black text-gray-900 tracking-tighter">{calculated.totalHandoffsCount}</span>
              <p className="text-[10px] font-semibold text-gray-400 mt-1">total escalated</p>
            </div>
          </div>
          <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center shrink-0">
            <img src={humanHandoff} className="w-full h-full object-cover scale-110" alt="" />
          </div>
        </div>

        {/* Token Cost Today */}
        <div className="bg-white p-6 rounded-[24px] border border-gray-100/80 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between min-h-[155px] relative overflow-hidden group">
          <div className="flex flex-col justify-between min-h-[102px]">
            <span className="text-[10px] font-black text-black uppercase">
              Token Cost {dateRange === "today" ? "Today" : `(${dateRangeLabels[dateRange] || dateRange})`}
            </span>
            <div>
              <span className="text-3xl font-black text-gray-900 tracking-tighter">{calculated.totalCostStr}</span>
              <p className="text-[10px] font-semibold text-gray-400 mt-1">computed usage</p>
            </div>
          </div>
          <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center shrink-0">
            <img src={tokenCostToday} className="w-full h-full object-cover scale-110" alt="" />
          </div>
        </div>
      </div>

      {/* Responsive Columns: Needs Attention (Live Alerts) & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">

        {/* ITEM 3: "NEEDS ATTENTION" LIVE INBOX CENTER */}
        <div className="bg-white rounded-[28px] border border-gray-100 p-6 lg:p-8 shadow-sm flex flex-col justify-between lg:col-span-7 h-[500px]">
          <div className="space-y-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 bg-blue-50/50 rounded-2xl flex items-center justify-center overflow-hidden shrink-0">
                  <img src={needsAttentionIcon} className="w-full h-full object-cover scale-110 rounded-2xl" alt="" />
                </div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-black text-black uppercase tracking-widest">Needs Attention</h3>
                  {handoffSessions.length > 0 && (
                    <span className="flex h-2.5 w-2.5 relative">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span>
                    </span>
                  )}
                </div>
              </div>
              <span className="text-[10px] font-black px-2 py-0.5 rounded bg-rose-50 text-rose-500 uppercase">
                {handoffSessions.length} Handoff Requests
              </span>
            </div>
            <p className="text-[11px] font-medium text-black leading-normal">
              Active widget chats currently flagged for escalation. Claim them to intervene.
            </p>
          </div>

          {/* Alert List Container */}
          <div className="flex-1 overflow-y-auto pr-1 space-y-3.5 custom-scrollbar min-h-0">
            {handoffSessions.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6 space-y-3">
                <div className="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center overflow-hidden">
                  <img src={operationalPeaceIcon} className="w-full h-full object-cover rounded-full" alt="" />
                </div>
                <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wider">Operational Peace</h4>
                <p className="text-[11px] font-medium text-gray-400 max-w-sm">
                  Your AI assistants are handling all conversations perfectly. No live claims are requested.
                </p>
              </div>
            ) : (
              handoffSessions.map((session) => (
                <div
                  key={session.session_id}
                  className="group flex items-center justify-between p-4 bg-slate-50/50 hover:bg-slate-50 border border-slate-100 rounded-2xl transition-all duration-200"
                >
                  <div className="flex items-center gap-3.5 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#83bbe1] font-extrabold text-xs flex items-center justify-center shrink-0">
                      {(session.summary || "U").substring(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-gray-900 text-xs truncate">
                          {session.summary || "Anonymous Session"}
                        </span>
                        {session.handoff_status === "requested" ? (
                          <span className="text-[9px] font-black text-rose-500 bg-rose-50 px-1.5 py-0.5 rounded uppercase">
                            Escalated
                          </span>
                        ) : (
                          <span className="text-[9px] font-black text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded uppercase">
                            Claimed ({session.agent_name || "Agent"})
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-3 text-[10px] text-gray-400 font-medium">
                        <span
                          className="flex items-center gap-1 relative cursor-help"
                          onMouseEnter={() => setHoveredSessionId(session.session_id)}
                          onMouseLeave={() => setHoveredSessionId(null)}
                        >
                          <Clock size={11} />
                          {getWaitTimeString(session.updated_at)}
                          {hoveredSessionId === session.session_id && (
                            <div style={{
                              position: 'absolute',
                              bottom: '100%',
                              left: '0',
                              marginBottom: '6px',
                              background: '#1e293b',
                              color: '#ffffff',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              whiteSpace: 'nowrap',
                              zIndex: 50,
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                              pointerEvents: 'none'
                            }}>
                              {new Date(session.updated_at * 1000).toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }) + ', ' + new Date(session.updated_at * 1000).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </div>
                          )}
                        </span>
                        <span className="truncate max-w-[200px]">
                          Reason: {session.handoff_reason || "Direct customer trigger"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Claims CTA */}
                  {session.handoff_status === "requested" ? (
                    <button
                      onClick={() => handleClaimChat(session.session_id)}
                      disabled={claimingId === session.session_id}
                      className="flex items-center gap-1.5 bg-gray-900 hover:bg-gray-800 active:scale-95 disabled:opacity-50 text-white text-[10px] font-black uppercase tracking-wider px-3.5 py-2.5 rounded-xl transition-all shadow-sm shrink-0"
                    >
                      <span>Claim</span>
                      <ArrowRight size={10} strokeWidth={2.5} />
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setActiveTab("human-agent-chat");
                        window.location.href = `/human-agent-chat?session_id=${session.session_id}`;
                      }}
                      className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 active:scale-95 text-white text-[10px] font-black uppercase tracking-wider px-3.5 py-2.5 rounded-xl transition-all shadow-sm shrink-0"
                    >
                      <span>View</span>
                      <ArrowRight size={10} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Navigation link footer */}
          <div className="pt-4 border-t border-gray-50 flex items-center justify-end">
            <button
              onClick={() => {
                setActiveTab("human-agent-chat");
                window.location.href = "/human-agent-chat";
              }}
              className="text-[10px] font-black text-blue-600 hover:text-blue-700 uppercase tracking-widest flex items-center gap-1 group transition-colors"
            >
              <span>View all in Inbox</span>
              <ChevronRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </div>

        {/* ITEM 4: "QUICK ACTIONS" MATRIX COMMAND GRID */}
        <div className="bg-white rounded-[28px] border border-gray-100 p-6 lg:p-8 shadow-sm flex flex-col justify-between lg:col-span-5 h-[500px]">
          <div className="space-y-4 mb-4">
            <h3 className="text-xs font-black text-black uppercase tracking-widest">Quick Actions</h3>
            <p className="text-[11px] font-medium text-black leading-normal">
              Execute routine chatbot setup, model integrations, and workspace adjustments instantly.
            </p>
          </div>

          {/* Action grid layout */}
          <div className="flex-1 grid grid-cols-2 gap-4 mt-2">
            {/* Action 1: API Tools */}
            <button
              onClick={() => {
                setActiveTab("api-tools");
                window.location.href = "/api-tools";
              }}
              className="group flex flex-col justify-between p-5 bg-blue-50/40 hover:bg-blue-50/70 rounded-2xl transition-all duration-200 text-left border border-blue-100/50 hover:border-blue-100 active:scale-98"
            >
              <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center transition-transform group-hover:scale-105 duration-200">
                <img src={quickActionsApiIcon} className="w-full h-full object-cover rounded-xl scale-110" alt="" />
              </div>
              <div>
                <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wider mb-1">API Tools</h4>
                <p className="text-[9px] font-medium text-gray-400 leading-snug">
                  Manage API tools and integrations.
                </p>
              </div>
            </button>

            {/* Action 2: Document Upload */}
            <button
              onClick={() => {
                setActiveTab("documents");
                window.location.href = "/documents";
              }}
              className="group flex flex-col justify-between p-5 bg-purple-50/40 hover:bg-purple-50/70 rounded-2xl transition-all duration-200 text-left border border-purple-100/50 hover:border-purple-100 active:scale-98"
            >
              <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center transition-transform group-hover:scale-105 duration-200">
                <img src={quickActionsUploadDocIcon} className="w-full h-full object-cover rounded-xl scale-110" alt="" />
              </div>
              <div>
                <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wider mb-1">Upload Doc</h4>
                <p className="text-[9px] font-medium text-gray-400 leading-snug">
                  Train your agents on custom files.
                </p>
              </div>
            </button>

            {/* Action 3: Add Integration */}
            <button
              onClick={() => {
                setActiveTab("integrations");
                window.location.href = "/integrations";
              }}
              className="group flex flex-col justify-between p-5 bg-amber-50/40 hover:bg-amber-50/70 rounded-2xl transition-all duration-200 text-left border border-amber-100/50 hover:border-amber-100 active:scale-98"
            >
              <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center transition-transform group-hover:scale-105 duration-200">
                <img src={quickActionsIntegrationIcon} className="w-full h-full object-cover rounded-xl scale-110" alt="" />
              </div>
              <div>
                <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wider mb-1">Integration</h4>
                <p className="text-[9px] font-medium text-gray-400 leading-snug">
                  Connect to Slack or WhatsApp APIs.
                </p>
              </div>
            </button>

            {/* Action 4: Invite Teammate */}
            <button
              onClick={() => {
                setActiveTab("contacts");
                window.location.href = "/user-management";
              }}
              className="group flex flex-col justify-between p-5 bg-green-50/40 hover:bg-green-50/70 rounded-2xl transition-all duration-200 text-left border border-green-100/50 hover:border-green-100 active:scale-98"
            >
              <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center transition-transform group-hover:scale-105 duration-200">
                <img src={quickActionsInviteIcon} className="w-full h-full object-cover rounded-xl scale-110" alt="" />
              </div>
              <div>
                <h4 className="font-bold text-gray-800 text-xs uppercase tracking-wider mb-1">Invite</h4>
                <p className="text-[9px] font-medium text-gray-400 leading-snug">
                  Add human agents or admins.
                </p>
              </div>
            </button>

          </div>

          <div className="pt-4 border-t border-gray-50 text-center">
            <span className="text-[9px] font-bold text-gray-300 uppercase tracking-widest">Workspace Dashboard</span>
          </div>
        </div>

      </div>

      {/* DYNAMIC "+ NEW AGENT" CREATION MODAL */}
      {modalOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[9999] flex items-center justify-center animate-fadeIn">
          <div className="bg-white rounded-[32px] border border-gray-100 p-8 w-full max-w-lg shadow-2xl relative animate-scaleUp">

            {/* Modal Header */}
            <button
              onClick={() => setModalOpen(false)}
              className="absolute right-6 top-6 p-2 text-gray-400 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-xl transition-all"
            >
              <X size={16} />
            </button>

            <div className="flex items-center gap-3.5 mb-6">
              <div className="w-11 h-11 bg-gray-900 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-gray-900/10">
                <Sparkles size={20} />
              </div>
              <div>
                <h3 className="font-black text-gray-900 text-sm uppercase tracking-widest">New AI Agent Protocol</h3>
                <p className="text-[11px] font-medium text-gray-400 mt-0.5">Initialize a dynamic AI agent identity</p>
              </div>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleCreateaiAgent} className="space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 tracking-widest uppercase block">
                  AI Agent Name *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Sales Onboarding Expert"
                  value={newAiAgentName}
                  onChange={(e) => setNewAiAgentName(e.target.value)}
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-sm font-semibold transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 tracking-widest uppercase block">
                  Directive Description
                </label>
                <textarea
                  placeholder="Describe the operational duties or role parameters..."
                  rows={3}
                  value={newAiAgentDesc}
                  onChange={(e) => setNewAiAgentDesc(e.target.value)}
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-gray-400 focus:border-gray-400 text-sm font-semibold resize-none transition-all"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="flex-1 py-3.5 bg-gray-50 hover:bg-gray-100 text-gray-600 font-bold text-xs uppercase tracking-widest rounded-xl transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creatingaiAgent || !newAiAgentName.trim()}
                  className="flex-1 py-3.5 bg-gray-900 hover:bg-gray-800 disabled:opacity-60 text-white font-bold text-xs uppercase tracking-widest rounded-xl transition-all shadow-sm flex items-center justify-center gap-2"
                >
                  {creatingaiAgent ? (
                    <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                  ) : (
                    <span>Deploy AI Agent</span>
                  )}
                </button>
              </div>
            </form>

          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
